'use client';

import { useEffect, useState } from 'react';
import { collection, doc, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { CecopLive, CecopProcesion } from '@/lib/types';
import { ProcesionMap, ProcesionLegend } from '@/components/cecop/ProcesionMap';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Expand } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Bloque "PROCESIÓN EN DIRECTO" del panel de Jartinet. Solo se pinta si hay una
 * procesión con `status: 'live'`; si no, no ocupa sitio.
 *
 * Las posiciones las escribe `modHeartbeat` en `cecop_live/{id}` a cada latido
 * del servidor (~15 s), así que el GPS refresca a ese ritmo, no continuo.
 */
export function ProcesionEnDirecto() {
  const [procesion, setProcesion] = useState<CecopProcesion | null>(null);
  const [live, setLive] = useState<CecopLive | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Solo igualdad sobre un campo → sin índice compuesto.
    const q = query(collection(db, 'cecop_procesiones'), where('status', '==', 'live'), limit(1));
    const u = onSnapshot(
      q,
      (snap) => setProcesion(snap.empty ? null : ({ id: snap.docs[0].id, ...(snap.docs[0].data() as any) })),
      () => setProcesion(null),
    );
    return () => u();
  }, []);

  useEffect(() => {
    if (!procesion?.id) { setLive(null); return; }
    const u = onSnapshot(
      doc(db, 'cecop_live', procesion.id),
      (snap) => setLive(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null),
      () => setLive(null),
    );
    return () => u();
  }, [procesion?.id]);

  if (!procesion) return null;

  const color = procesion.color || '#d4af37';
  const entries = live?.entries || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border p-4"
      style={{ borderColor: `${color}55`, background: `linear-gradient(135deg, ${color}1c, transparent 60%)` }}
    >
      <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full blur-3xl" style={{ background: `${color}26` }} />

      <div className="relative flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/20 px-2 py-0.5 text-[0.55rem] font-black tracking-widest text-rose-300">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
          EN DIRECTO
        </span>
        <h2 className="min-w-0 flex-1 truncate text-base font-bold">{procesion.titulo}</h2>
        <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-xs" onClick={() => setExpanded(true)}>
          <Expand className="h-3.5 w-3.5" />
          Ampliar
        </Button>
      </div>

      {/* Mapa a altura fija: el ancho lo marca la proporción del recorte.
          Con `interactive` se puede acercar con la rueda y arrastrar para
          moverse por el recorrido. */}
      <div className="relative mt-3 flex justify-center">
        <ProcesionMap
          map={procesion.map}
          viewport={procesion.viewport}
          color={color}
          iglesia={procesion.iglesia}
          estacion={procesion.estacion}
          ida={procesion.ida}
          vuelta={procesion.vuelta}
          live={entries}
          fixedHeight={440}
          showLabels
          interactive
        />
      </div>

      <div className="relative mt-3 flex flex-col items-center gap-1">
        <ProcesionLegend color={color} />
        <p className="text-[0.6rem] text-muted-foreground">Arrastra para moverte · rueda del ratón para acercar</p>
      </div>

      {/* Vista ampliada a pantalla casi completa */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-rose-400" />
              {procesion.titulo}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <ProcesionMap
              map={procesion.map}
              viewport={procesion.viewport}
              color={color}
              iglesia={procesion.iglesia}
              estacion={procesion.estacion}
              ida={procesion.ida}
              vuelta={procesion.vuelta}
              live={entries}
              fixedHeight={620}
              showLabels
              interactive
            />
          </div>
          <ProcesionLegend color={color} />
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
